import { useApiMutation } from "@gladis/api-client";
import {
  Button,
  Dialog,
  Label,
  MoneyInput,
  parseEurInput,
  SelectField,
  TextField,
} from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { SAVINGS_KEY } from "./AccountList.js";

export interface AccountFormMember {
  userId: string;
  displayName: string;
}

/**
 * Add a savings account with its opening balance (FR-001 to FR-008).
 *
 * IT IS A MODAL, for the reason `CardForm` and `ExpenseForm` are: an inline panel pushed the stat
 * cards, the chart and the account list down by its own height the moment it opened, so the click
 * that said "add an account" moved everything the member was looking at. It was also the last
 * primary "add" action in the application that was not a dialog, which made the Savings page the
 * one page where that control behaved differently from every other.
 *
 * THE HEADING AND THE SENTENCE ARE `Dialog` PROPS. The frame and the `<h2>` this file used to draw
 * are the dialog's now; the words are still savings'.
 *
 * THE OWNER SELECTOR IS FED BY THE MEMBER LIST, not by anything this module infers. FR-005 requires
 * the authoritative list — the one 005's `listMembers` returns — and the server validates the
 * choice against it again on submit. The dropdown is a convenience, never the enforcement: a
 * request naming somebody who left comes back `SAV_OWNER_NOT_MEMBER`.
 *
 * The date defaults to today and can be moved either way. Backdating is silent (FR-012) — it is the
 * supported way to enter history, and warning about it would train people to dismiss warnings.
 * Forward-dating warns (FR-013), and that is the only warning this module raises.
 */
export function AccountForm({
  open,
  onClose,
  members,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  members: readonly AccountFormMember[];
  onCreated?: () => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState("");
  const [ownership, setOwnership] = useState<"joint" | "individual">("joint");
  const [ownerId, setOwnerId] = useState<string>("");
  const [balance, setBalance] = useState("");
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));

  /*
   * THE FIELDS ARE CLEARED WHEN THE DIALOG OPENS, and that is a consequence of it staying mounted.
   *
   * A native `<dialog>` cannot be conditionally rendered — `Dialog` calls `showModal()` from an
   * effect on an element it already holds a ref to, and an element appearing in the same commit as
   * `open` has no first render in which to be closed. So this component no longer unmounts between
   * uses, and without this its `useState` would hold whatever the LAST account was called: the
   * defect 016 records for the investments dialogs, where a second transaction opened carrying the
   * first one's fee.
   *
   * On OPEN rather than on close, so a member who dismissed by accident is not the one punished for
   * it — and because `members` may still have been loading when this component first mounted, which
   * is what `ownerId` is derived from.
   */
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName("");
      setOwnership("joint");
      setOwnerId(members[0]?.userId ?? "");
      setBalance("");
      setAsOfDate(new Date().toISOString().slice(0, 10));
    }
  }

  const create = useApiMutation<{ id: string }, Record<string, unknown>>(
    (client, input) => client.post<{ id: string }>("/savings/accounts", input),
    { invalidates: [SAVINGS_KEY] },
  );

  /*
   * Compared as STRINGS, not as Dates.
   *
   * ISO dates sort lexicographically, so this is exact and has no timezone in it. Building two
   * `Date`s and comparing them would reintroduce, in the browser, the same class of off-by-one the
   * server side spent effort eliminating — and the browser's timezone is far less predictable than
   * the server's.
   */
  const isFuture = asOfDate > new Date().toISOString().slice(0, 10);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const amount = parseEurInput(balance);
    if (amount === null) return;

    create.mutate(
      {
        name,
        ownership,
        ownerId: ownership === "individual" ? ownerId : null,
        openingBalance: amount,
        asOfDate,
      },
      { onSuccess: () => onCreated?.() },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("savings.form.heading")}
      description={t("savings.form.hint")}
    >
      <form onSubmit={submit}>
        <div className="grid gap-4">
          <div>
            <Label htmlFor="savings-name">{t("savings.form.name")}</Label>
            <TextField
              id="savings-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("savings.form.namePlaceholder")}
              required
            />
          </div>

          <fieldset>
            <legend className="text-sm text-muted">{t("savings.form.heldBy")}</legend>
            <label className="mr-4 inline-flex items-center gap-2">
              <input
                type="radio"
                name="ownership"
                value="joint"
                checked={ownership === "joint"}
                onChange={() => setOwnership("joint")}
              />
              {t("savings.form.joint")}
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="ownership"
                value="individual"
                checked={ownership === "individual"}
                onChange={() => setOwnership("individual")}
              />
              {t("savings.form.individual")}
            </label>
          </fieldset>

          {ownership === "individual" ? (
            <div>
              <Label htmlFor="savings-owner">{t("savings.form.owner")}</Label>
              <SelectField
                id="savings-owner"
                value={ownerId}
                onChange={(event) => setOwnerId(event.target.value)}
                required
              >
                {members.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.displayName}
                  </option>
                ))}
              </SelectField>
            </div>
          ) : null}

          <div>
            <Label htmlFor="savings-balance">{t("savings.form.currentBalance")}</Label>
            <MoneyInput
              id="savings-balance"
              value={balance}
              onValueChange={setBalance}
              placeholder="0,00"
              required
            />
          </div>

          <div>
            <Label htmlFor="savings-date">{t("savings.form.asOf")}</Label>
            <TextField
              id="savings-date"
              type="date"
              value={asOfDate}
              onChange={(event) => setAsOfDate(event.target.value)}
              required
            />
            {/*
             * The only warning this module raises (FR-013). Advisory: it blocks nothing, it is
             * recorded nowhere, and the server neither knows nor cares that it was shown.
             */}
            {isFuture ? (
              <p className="mt-2 text-sm text-muted" role="status">
                {t("savings.form.futureDate")}
              </p>
            ) : null}
          </div>

          {create.isError ? (
            <p className="text-sm text-muted" role="alert">
              {errorSentence(create.error)}
            </p>
          ) : null}
        </div>

        {/* Cancel beside Submit — see the note in `CardForm`: the three other ways out of a dialog
            are all invisible. */}
        <div className="mt-5 flex justify-end gap-3">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? t("savings.form.submitting") : t("savings.form.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
